<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import api from '@/api';
import { MAX_SHAREPOINT_SITE_URLS } from '@/constants';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Header from '@/components/layouts/header.vue';
import { SharepointSite, UserDocumentStorageStatus } from '@/types';

const { createSnackbar } = useSnackbar();

const sharepointSites = ref<SharepointSite[]>([]);
const storageStatus = ref<UserDocumentStorageStatus>();

// storageStatus を取得できない場合、またはステータスがsyncingの場合、編集ボタンは無効になります
const isEditButtonDisabled = computed(
  () => !storageStatus.value || storageStatus.value?.status === 'syncing',
);

onMounted(async () => {
  try {
    sharepointSites.value = await api.getSharepointSites();
  } catch (error) {
    createSnackbar({
      message: 'Sharepointサイトの取得に失敗しました',
      type: 'error',
    });
    // eslint-disable-next-line no-console
    console.error(error);

    // サイト情報の取得に失敗した場合は、storageStatusを取得しない
    return;
  }
  storageStatus.value = await api.getTeamStorageStatus('share_point');
});
</script>

<template>
  <div class="sharepoint-sites">
    <Header title="サイトURL情報" header-width="100%" is-one-line />
    <div class="content">
      <div class="sharepoint-site-list-content">
        <div class="content-header">
          <div class="title">
            <div class="c-title-bold--m">サイト</div>
            <div class="c-text-regular--s sharepoint-site-url-count">
              ・{{ sharepointSites.length }}件/{{ MAX_SHAREPOINT_SITE_URLS }}件
            </div>
          </div>
          <component
            :is="isEditButtonDisabled ? 'button' : 'router-link'"
            :to="{ name: 'sharepointSitesEdit' }"
            class="c-btn c-btn--auto c-btn--AnewsPrimary"
            :class="{ disabled: isEditButtonDisabled }"
            :disabled="isEditButtonDisabled"
          >
            編集
          </component>
        </div>
        <div class="sharepoint-site-url-list">
          <div class="title c-text-regular--s">URL</div>
          <div
            v-for="(sharepointSite, i) in sharepointSites"
            :key="i"
            class="sharepoint-site-url c-text-regular--m"
          >
            {{ sharepointSite.site_url }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sharepoint-sites {
  width: 100%;
  padding: 0 !important;
}
.sharepoint-site-list-content {
  padding: 24px;
}
.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  .title {
    display: flex;
    align-items: center;
    gap: 4px;
    .sharepoint-site-url-count {
      color: $color-gray600;
    }
  }
}
.sharepoint-site-url-list {
  background-color: $color-white;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  padding: 16px;
  .title {
    padding: 8px 0;
    border-bottom: 1px solid $color-gray400;
  }
}
.sharepoint-site-url {
  padding: 8px;
  min-height: 32px;
  border-bottom: 1px solid $color-gray400;
  display: flex;
  align-items: center;
  word-break: break-all;
  &:hover:not(:has(.action-icon:hover)) {
    background: $color-gray200;
  }
}
</style>
